import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { VersionConfigService } from '@app/core/services/version-config';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import {
  CustomEncoder,
  generatePlatformQueryParameters,
} from '@app/core/async-services/http/http.util';
import { Observable } from 'rxjs';
import * as Pubnub from 'pubnub';
import { Platform } from '@ionic/angular';
import { API } from '@app/core/models';
import { ApiVersionedRoute } from './api-versions';
import { map } from 'rxjs/operators';
import {
  ChannelData,
  CreateChatChannelBody,
  NewChatRecipient,
} from './interfaces/chat.interface';
import { PubnubGrantToken } from '@app/core/models/pubnub.interface';
import { HttpOptions } from '@app/core/models/api.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatHttpService {
  constructor(
    private _versionConfigService: VersionConfigService,
    private _coreDataService: CoreDataService,
    private _platform: Platform,
  ) {}

  getChatContacts() {
    const initialOpts: API.HttpOptions = {
      headers: new HttpHeaders(),
      withCredentials: true,
    };
    const finalOpts = generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      initialOpts,
      this._platform.is('capacitor'),
    );
    return this._coreDataService.get<API.ChatContactsSuccess>(
      `${ApiVersionedRoute.chat}/contacts`,
      finalOpts,
    );
  }

  getChatChannelForUsers(users: any) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body = {
      people: users,
    };

    return this._coreDataService.post<API.ChatChannelSuccess>(
      `${ApiVersionedRoute.chat}/channel`,
      body,
      options,
    );
  }

  createChatChannelForUsers(
    selectedUsers: NewChatRecipient[],
  ): Observable<API.ChatChannelSuccess> {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body: CreateChatChannelBody = {
      people: selectedUsers.map((user) => ({
        userID: user.userID,
        userType: user.userType,
      })),
    };

    return this._coreDataService.post<API.ChatChannelSuccess>(
      `${ApiVersionedRoute.chat}/channel/create`,
      body,
      options,
    );
  }

  sendMessage(publishParams: Pubnub.PublishParameters) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body = publishParams;

    return this._coreDataService.post<API.ChatChannelSuccess>(
      `${ApiVersionedRoute.chat}/message/create`,
      body,
      options,
    );
  }

  getChannelData(
    channel: string,
    requestDirection: 'last' | 'newer' | 'older' = 'last',
    lastViewedMessageId: string | null = null,
  ) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body: any = { channel, requestDirection, lastViewedMessageId };

    return this._coreDataService.post<API.Response<ChannelData>>(
      `${ApiVersionedRoute.chat}/users`,
      body,
      options,
    );
  }

  markChannelRead(channel: string) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body: any = { channel };

    return this._coreDataService.post<void>(
      `${ApiVersionedRoute.chat}/messages/read`,
      body,
      options,
    );
  }

  getInboxChannel() {
    return this._coreDataService.get<API.GetInboxChannelSuccess>(
      `${ApiVersionedRoute.chat}/inbox-channel`,
      this._coreDataService.getHttpOptions(),
    );
  }

  getChannels(config: { archived: boolean; pageNumber: number }) {
    const headers = new HttpHeaders();
    const options: HttpOptions = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.getHttpParams({
      requestPageNumber: config.pageNumber,
      requestPageLimit: this._versionConfigService.messagesPageLimit,
    });

    return this._coreDataService.get<API.GetChannelsSuccess>(
      `${ApiVersionedRoute.chat}/channels/${config.archived}`,
      options,
    );
  }

  archiveChannel(channelName: string) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};
    const body: API.ArchiveChannelBody = {
      channel: channelName,
    };

    return this._coreDataService.post<API.ArchiveChannelSuccess>(
      `${ApiVersionedRoute.chat}/channel/archive`,
      body,
      options,
    );
  }

  grantToken(
    channels: string[],
    webUserId: string,
  ): Observable<PubnubGrantToken> {
    const options = this._coreDataService.getHttpOptions({
      channels,
      webUserId,
    });
    return this._coreDataService
      .get<API.Response<PubnubGrantToken>>(
        `${ApiVersionedRoute.chat}/grant-token`,
        options,
      )
      .pipe(map((response) => response.payload as PubnubGrantToken));
  }

  revokeToken(token: string): Observable<Record<string, unknown>> {
    const options = this._coreDataService.getHttpOptions();
    const body = { token };
    return this._coreDataService.post(
      `${ApiVersionedRoute.chat}/revoke-token`,
      body,
      options,
    );
  }
}
